/**
 * Sync Engine - Offline-first bidirectional sync with Supabase
 *
 * Features:
 * - Offline queue: Changes tracked locally when offline
 * - Sync up: Push local changes to Supabase
 * - Sync down: Pull remote changes to local
 * - Conflict resolution: Last-write-wins with detection
 * - Real-time: Live updates via Supabase Realtime
 */

import { RealtimeChannel } from '@supabase/supabase-js';
import { supabase, checkAndRecoverAuth, getAuthHealthStatus, type AuthHealthStatus } from '../lib/supabase';
import { restSelect, restSelectAll, restSelectSingle, restInsert, restUpdate, restDelete, getUserId } from '../lib/supabaseRest';
import { db } from '../db/database';
import { eventBus } from '../events';
import { addBreadcrumb, captureError } from '../lib/sentry';
import { applyRemoteUpdate, initYDocFromState, encodeYDocState } from '../services/BlockYjsService';

// Block types that use Yjs CRDT for conflict resolution (must match DataRepository.ts)
const TEXT_CRDT_TYPES = ['text', 'heading', 'heading1', 'heading2', 'heading3', 'list', 'checklist', 'tasklist', 'code', 'callout'];

// Sync retry configuration
const MAX_RETRIES = 10; // Maximum number of retries before marking as permanently failed
const BASE_BACKOFF_MS = 1000; // Base delay for exponential backoff (1 second)
const MAX_BACKOFF_MS = 60000; // Maximum backoff delay (1 minute)

/**
 * Field name transformation utilities for IndexedDB (camelCase) <-> Supabase (snake_case) conversion
 */

/**
 * Convert camelCase string to snake_case
 * Example: "createdAt" -> "created_at"
 */
function toSnakeCase(str: string): string {
  return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
}

/**
 * Convert snake_case string to camelCase
 * Example: "created_at" -> "createdAt"
 */
function toCamelCase(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

/**
 * Transform all keys in an object from camelCase to snake_case
 * Used when sending data from IndexedDB (camelCase) to Supabase (snake_case)
 */
function transformKeysToSnakeCase(obj: any): any {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map(transformKeysToSnakeCase);
  if (typeof obj !== 'object') return obj;

  const transformed: any = {};
  for (const [key, value] of Object.entries(obj)) {
    const snakeKey = toSnakeCase(key);
    transformed[snakeKey] = typeof value === 'object' && value !== null && !Array.isArray(value)
      ? transformKeysToSnakeCase(value)
      : value;
  }
  return transformed;
}

/**
 * Transform all keys in an object from snake_case to camelCase
 * Used when receiving data from Supabase (snake_case) for IndexedDB (camelCase)
 */
function transformKeysToCamelCase(obj: any): any {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map(transformKeysToCamelCase);
  if (typeof obj !== 'object') return obj;

  const transformed: any = {};
  for (const [key, value] of Object.entries(obj)) {
    const camelKey = toCamelCase(key);
    transformed[camelKey] = typeof value === 'object' && value !== null && !Array.isArray(value)
      ? transformKeysToCamelCase(value)
      : value;
  }
  return transformed;
}

/**
 * Transform a Supabase block row to its camelCase local shape, normalizing the
 * `title` field. Supabase returns `title: null` for blocks with no title, but
 * locally we represent "no title field" as undefined — a null title leaks into
 * controlled <input value={null}> and triggers a React warning. Route every
 * Supabase->local block hydration through this helper.
 */
function transformBlockToLocal(block: any): any {
  const localBlock = transformKeysToCamelCase(block);
  if (localBlock.title === null) {
    localBlock.title = undefined;
  }
  return localBlock;
}

/**
 * Timeout wrapper for async operations
 * Throws an error if the operation takes longer than the specified timeout
 */
async function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  operationName: string
): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout>;

  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error(`TIMEOUT: ${operationName} took longer than ${timeoutMs}ms`));
    }, timeoutMs);
  });

  try {
    const result = await Promise.race([promise, timeoutPromise]);
    clearTimeout(timeoutId!);
    return result;
  } catch (error) {
    clearTimeout(timeoutId!);
    throw error;
  }
}

// Note: getSessionFromLocalStorage removed - now using getUserId() from supabaseRest.ts

interface SyncQueueEntry {
  id: string;
  table: string;
  recordId: string;
  operation: 'create' | 'update' | 'delete';
  data: any;
  timestamp: Date;
  synced: boolean;
}

interface SyncStatus {
  isOnline: boolean;
  isSyncing: boolean;
  lastSyncAt: Date | null;
  pendingChanges: number;
  failedChanges: number;
  queuedChanges: SyncQueueEntry[];
  oldestPending: Date | null;
  authHealth: AuthHealthStatus;
}

interface SyncQueueStats {
  pending: number;
  failed: number;
  retrying: number;
  oldestPending: Date | null;
  oldestFailed: Date | null;
}

class SyncEngine {
  private syncInterval: ReturnType<typeof setInterval> | null = null;
  private realtimeChannel: RealtimeChannel | null = null;
  private isInitialized = false;
  private syncInProgress = false;
  private _syncStartedAt = 0;
  private listeners: Set<(status: SyncStatus) => void> = new Set();
  private dataChangeListeners: Set<() => void> = new Set();
  private notifyTimeout: ReturnType<typeof setTimeout> | null = null;
  private pendingUpdates: any[] = [];
  private updateBatchTimeout: ReturnType<typeof setTimeout> | null = null;
  private cachedStatus: SyncStatus | null = null;
  private statusCacheTimeout: ReturnType<typeof setTimeout> | null = null;
  private visibilityHandler: (() => void) | null = null;
  private isFirstSyncOfSession = true; // Track if this is the first sync of the session
  private syncCount = 0; // Track number of syncs for periodic full sync
  // D1: record_ids with a locally-queued change not yet confirmed synced.
  // Best-effort, in-memory guard so realtime UPDATEs don't clobber a row whose
  // local edit hasn't pushed. The authoritative guard is the sync_queue check
  // in syncDown(); this covers the faster realtime path between sync cycles.
  private locallyDirty: Set<string> = new Set();
  // D8: self-echo guard. After we push a record, Supabase realtime echoes our
  // own write straight back. "table:id" -> timestamp(ms); a realtime event for
  // a record we wrote within RECENTLY_WRITTEN_TTL_MS is our own echo and is
  // skipped (mirrors mobile's recentlyWritten / isSelfEcho).
  private recentlyWritten: Map<string, number> = new Map();
  private static readonly RECENTLY_WRITTEN_TTL_MS = 3000;

  constructor() {
    // Set up visibility handler immediately (not dependent on auth)
    // This ensures sync triggers when user switches back to tab
    this.setupVisibilityHandler();
  }

  /**
   * Set up visibility change handler to sync when tab becomes visible
   * Called from constructor so it works even before auth completes
   */
  private setupVisibilityHandler(): void {
    if (this.visibilityHandler) return; // Already set up

    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        console.log('👁️ Tab became visible, syncing changes since last cursor...');
        // Only sync if we're initialized (authenticated)
        if (this.isInitialized) {
          // Pull changes since last sync — Realtime handles instant updates now
          // so we don't need to reset the cursor (no more aggressive full re-download)
          this.sync({ skipAuthCheck: true }).then(() => {
            console.log('👁️ Visibility-triggered sync completed');
          }).catch(err => {
            console.error('👁️ Visibility-triggered sync failed:', err);
          });
        } else {
          console.log('👁️ Not initialized yet, skipping sync');
        }
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);
    console.log('👁️ Visibility handler registered');
  }

  /**
   * Initialize sync engine
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    console.log('🔄 Initializing Sync Engine...');

    // Ensure visibility handler is set up (in case constructor didn't run)
    this.setupVisibilityHandler();

    // Check if user is authenticated via localStorage (bypass Supabase client)
    const userId = getUserId();
    if (!userId) {
      console.warn('⚠️ Not authenticated (no user ID in localStorage), sync disabled');
      return;
    }
    console.log('✅ User authenticated via localStorage:', userId.substring(0, 8) + '...');

    // Mark as initialized FIRST so visibility handler can trigger syncs
    this.isInitialized = true;
    console.log('✅ Sync Engine initialized');

    // Set up periodic sync (every 60 seconds as safety net — Realtime is now primary)
    this.syncInterval = setInterval(() => {
      this.sync();
    }, 60000);

    // Initial sync - skip auth check since we just logged in
    // This avoids slow checkAndRecoverAuth() call on fresh login
    await this.sync({ skipAuthCheck: true });

    // Set up real-time subscriptions (primary sync mechanism — awaited, retried on failure)
    try {
      await this.setupRealtime();
    } catch (err) {
      console.warn('⚠️ Realtime setup failed, will retry in 10s:', err);
      setTimeout(() => this.setupRealtime().catch(e => console.warn('⚠️ Realtime retry failed:', e)), 10000);
    }
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }

    if (this.realtimeChannel) {
      supabase.removeChannel(this.realtimeChannel);
      this.realtimeChannel = null;
    }

    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }

    if (this.notifyTimeout) {
      clearTimeout(this.notifyTimeout);
      this.notifyTimeout = null;
    }

    if (this.updateBatchTimeout) {
      clearTimeout(this.updateBatchTimeout);
      this.updateBatchTimeout = null;
    }

    if (this.statusCacheTimeout) {
      clearTimeout(this.statusCacheTimeout);
      this.statusCacheTimeout = null;
    }

    this.pendingUpdates = [];
    this.cachedStatus = null;
    this.isInitialized = false;
    this.isFirstSyncOfSession = true;
    this.syncCount = 0;
    console.log('🛑 Sync Engine destroyed');
  }

  /**
   * Alias for destroy() - stop the sync engine
   */
  stop(): void {
    this.destroy();
  }

  /**
   * Check if local IndexedDB objects table is empty
   * Used to detect cleared/corrupted database scenarios
   */
  private async isLocalDBEmpty(): Promise<boolean> {
    const count = await db.objects.count();
    return count === 0;
  }

  /**
   * Add a change to the sync queue
   */
  async queueChange(
    table: string,
    operation: 'create' | 'update' | 'delete',
    recordId: string,
    data: any
  ): Promise<void> {
    console.log(`[SyncEngine.queueChange] table: ${table}, operation: ${operation}, recordId: ${recordId}`);
    if (table === 'blocks') {
      console.log(`[SyncEngine.queueChange] Block data content length: ${data.content?.length || 0}`);
      console.log(`[SyncEngine.queueChange] Block content preview: ${data.content?.substring(0, 100) || '(empty)'}`);
    }

    const userId = getUserId();
    if (!userId) return;

    // Transform data from camelCase (IndexedDB) to snake_case (Supabase)
    const transformedData = transformKeysToSnakeCase(data);
    console.log(`[SyncEngine.queueChange] Data transformed to snake_case for Supabase`);

    // Add to Supabase sync queue using direct REST API
    const { data: insertedData, error: insertError } = await restInsert('sync_queue', {
      user_id: userId,
      table_name: table,
      record_id: recordId,
      operation,
      data: transformedData, // Use transformed snake_case data
      timestamp: new Date().toISOString(),
      synced: false,
      device_id: this.getDeviceId()
    });

    if (insertError) {
      console.error(`[SyncEngine.queueChange] Error inserting to sync_queue:`, insertError);
    } else if (!insertedData || insertedData.length === 0) {
      console.error(`[SyncEngine.queueChange] RLS rejected insert for ${table}:${recordId} - no data returned`);
    } else {
      console.log(`[SyncEngine.queueChange] Successfully queued ${table}:${recordId}`, insertedData[0].id);
    }

    // D1: mark this record dirty so realtime echoes don't clobber it before push.
    this.locallyDirty.add(recordId);

    this.notifyListeners();
  }

  /**
   * Main sync function - bidirectional sync
   * @param options.skipAuthCheck - Skip auth check for quick syncs (e.g., visibility triggered)
   */
  async sync(options: { skipAuthCheck?: boolean } = {}): Promise<void> {
    if (this.syncInProgress) {
      // Safety valve: if sync has been "in progress" for more than 60 seconds,
      // it's stuck. Force-reset so we can try again.
      const stuckMs = Date.now() - (this._syncStartedAt || 0);
      if (stuckMs > 60000) {
        console.warn(`⚠️ Sync has been stuck for ${Math.round(stuckMs / 1000)}s, force-resetting syncInProgress`);
        this.syncInProgress = false;
      } else {
        console.log('⏸️ Sync already in progress, skipping');
        return;
      }
    }

    if (!navigator.onLine) {
      console.log('📡 Offline, sync skipped');
      return;
    }

    // Quick check: do we have a user ID in localStorage?
    const userId = getUserId();
    if (!userId) {
      console.log('⏸️ No user ID in localStorage, sync skipped');
      return;
    }

    this.syncInProgress = true;
    this._syncStartedAt = Date.now();
    this.notifyListeners();

    try {
      console.log('🔄 Starting sync...');

      // Step 0: Check and recover auth if needed (skip for quick syncs)
      if (!options.skipAuthCheck) {
        const authStatus = await checkAndRecoverAuth();
        if (authStatus === 'needs_login') {
          console.warn('⚠️ Auth recovery failed, user needs to login. Sync aborted.');
          this.notifyListeners(); // Update UI with auth status
          return;
        }
        if (authStatus === 'recovered') {
          console.log('✅ Auth was recovered automatically, proceeding with sync');
        }
      } else {
        console.log('⏩ Skipping auth check (quick sync)');
      }

      // Step 1: Sync up (local → server)
      await this.syncUp();

      // Step 2: Sync down (server → local)
      await this.syncDown();

      console.log('✅ Sync complete');
    } catch (error) {
      console.error('❌ Sync failed:', error);
    } finally {
      this.syncInProgress = false;
      this.notifyListeners();
    }
  }

  /**
   * Sync up: Push local changes to Supabase
   */
  private async syncUp(): Promise<void> {
    console.log('📤 [syncUp] Starting...');

    // Get user ID directly from localStorage - bypasses stuck Supabase auth client
    console.log('📤 [syncUp] Getting user from localStorage...');
    const userId = getUserId();
    if (!userId) {
      console.log('📤 [syncUp] No user in localStorage, returning');
      return;
    }
    console.log('📤 [syncUp] User found:', userId);

    // Get unsynced changes from queue using direct REST API
    console.log('📤 [syncUp] Fetching sync_queue...');
    addBreadcrumb('Sync started', 'sync', { direction: 'up' });
    const { data: queue, error } = await withTimeout(
      restSelect('sync_queue', {
        select: '*',
        eq: { user_id: userId, synced: false },
        order: 'timestamp.asc'
      }),
      10000,
      'syncUp.fetchQueue'
    );
    console.log('📤 [syncUp] sync_queue fetch complete');

    if (error) {
      console.error('Error fetching sync queue:', error);
      return;
    }

    if (!queue || queue.length === 0) {
      console.log('📤 No changes to sync up');
      return;
    }

    console.log(`📤 Syncing ${queue.length} changes to server...`);

    // Deduplicate: For multiple updates to the same record, keep only the latest
    // This prevents sending stale data when user types quickly
    const deduplicatedQueue = this.deduplicateQueue(queue);
    console.log(`📤 After deduplication: ${deduplicatedQueue.length} unique changes`);

    // Sort queue to ensure objects are synced before contexts
    // This prevents foreign key constraint violations
    const sortedQueue = this.sortQueueByDependencies(deduplicatedQueue);

    // Track which objects have been successfully synced in this batch
    // This helps skip child entries (properties/blocks) if their parent wasn't synced yet
    const syncedObjectIds = new Set<string>();

    // Pre-compute which object IDs have pending CREATE entries that will be skipped
    const skippedObjectCreates = new Set<string>();
    for (const entry of sortedQueue) {
      if (entry.table_name === 'objects' && entry.operation === 'create') {
        // A create is never permanently skipped (it carries brand-new content
        // with no other home) — only its backoff window can delay it.
        const willSkip = entry.next_retry_at && new Date(entry.next_retry_at) > new Date();
        if (willSkip) {
          skippedObjectCreates.add(entry.record_id);
        }
      }
    }

    for (const entry of sortedQueue) {
      // Skip entries that have exceeded max retries — but NEVER drop a create.
      // An INSERT in the queue is brand-new user content that exists nowhere
      // else, so we retry it forever (mirrors the mobile fix that closed the
      // stranded-INSERT data loss). UPDATE/DELETE are still capped.
      if (entry.operation !== 'create' && entry.retry_count >= MAX_RETRIES) {
        console.warn(`⏭️ Skipping ${entry.table_name}:${entry.record_id} - exceeded max retries (${entry.retry_count})`);
        continue;
      }

      // Skip entries that are not yet due for retry (exponential backoff)
      if (entry.next_retry_at && new Date(entry.next_retry_at) > new Date()) {
        console.log(`⏳ Skipping ${entry.table_name}:${entry.record_id} - not yet due for retry`);
        continue;
      }

      // For blocks/properties, skip if parent object is in skipped creates (will retry later)
      if ((entry.table_name === 'blocks' || entry.table_name === 'properties') && entry.data?.object_id) {
        if (skippedObjectCreates.has(entry.data.object_id)) {
          console.log(`⏭️ Skipping ${entry.table_name}:${entry.record_id} - parent object ${entry.data.object_id} sync is delayed`);
          continue;
        }
      }

      try {
        await withTimeout(
          this.applySyncEntry(entry),
          15000,
          `applySyncEntry(${entry.table_name}:${entry.record_id})`
        );

        // Track successfully synced objects
        if (entry.table_name === 'objects') {
          syncedObjectIds.add(entry.record_id);
        }

        // Mark as synced and clear retry info using direct REST API
        await restUpdate('sync_queue', {
          synced: true,
          last_error: null,
          retry_count: entry.retry_count || 0
        }, { eq: { id: entry.id } }, { returning: false });

        // D1: record is now confirmed synced — drop the dirty guard.
        this.locallyDirty.delete(entry.record_id);

      } catch (error: any) {
        const errorMessage = error?.message || String(error);
        const newRetryCount = (entry.retry_count || 0) + 1;

        // Calculate next retry time with exponential backoff
        const backoffMs = Math.min(
          BASE_BACKOFF_MS * Math.pow(2, newRetryCount - 1),
          MAX_BACKOFF_MS
        );
        const nextRetryAt = new Date(Date.now() + backoffMs).toISOString();

        // Never permanently fail a create — it carries the only copy of new
        // content. Keep retrying it forever (backoff is capped at MAX_BACKOFF_MS,
        // so it retries roughly every minute) while still recording the error so
        // it surfaces in the failedChanges count. UPDATE/DELETE still cap out.
        const isCreate = entry.operation === 'create';
        if (!isCreate && newRetryCount >= MAX_RETRIES) {
          // Mark as permanently failed using direct REST API
          console.error(`❌ Entry ${entry.id} permanently failed after ${MAX_RETRIES} retries:`, errorMessage);
          await restUpdate('sync_queue', {
            retry_count: newRetryCount,
            last_error: errorMessage,
            failed_at: new Date().toISOString()
          }, { eq: { id: entry.id } }, { returning: false });
        } else {
          // Update retry count and schedule next retry using direct REST API
          console.warn(`⚠️ Entry ${entry.id} failed (retry ${newRetryCount}${isCreate ? '' : '/' + MAX_RETRIES}), next retry in ${backoffMs}ms:`, errorMessage);
          await restUpdate('sync_queue', {
            retry_count: newRetryCount,
            last_error: errorMessage,
            next_retry_at: nextRetryAt
          }, { eq: { id: entry.id } }, { returning: false });
        }
      }
    }
  }

  /**
   * Deduplicate sync queue entries
   * For multiple updates to the same record, keep only the latest one
   * Also marks older duplicate entries as synced to clean them up
   */
  private deduplicateQueue(queue: any[]): any[] {
    // Group by table_name + record_id + operation
    const latestByKey = new Map<string, any>();
    const duplicateIds: string[] = [];

    for (const entry of queue) {
      const key = `${entry.table_name}:${entry.record_id}:${entry.operation}`;
      const existing = latestByKey.get(key);

      if (existing) {
        // Keep the one with the later timestamp
        if (new Date(entry.timestamp) > new Date(existing.timestamp)) {
          duplicateIds.push(existing.id);
          latestByKey.set(key, entry);
        } else {
          duplicateIds.push(entry.id);
        }
      } else {
        latestByKey.set(key, entry);
      }
    }

    // D5: coalesce an update into a still-pending create for the same record.
    // create and update are distinct keys, so they survive the dedup above as
    // two rows — and the update could cap out and be dropped while the create
    // is still stuck, losing the edit. Fold the update's data into the create
    // payload (create then ships the latest content) and retire the update row.
    // Apply latest update last so it wins on overlapping fields.
    const updateEntries = Array.from(latestByKey.values())
      .filter(e => e.operation === 'update')
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    for (const upd of updateEntries) {
      const createKey = `${upd.table_name}:${upd.record_id}:create`;
      const createEntry = latestByKey.get(createKey);
      if (createEntry) {
        createEntry.data = { ...(createEntry.data || {}), ...(upd.data || {}) };
        const updKey = `${upd.table_name}:${upd.record_id}:update`;
        latestByKey.delete(updKey);
        duplicateIds.push(upd.id);
      }
    }

    // Mark duplicate entries as synced (async, don't wait) using direct REST API
    if (duplicateIds.length > 0) {
      console.log(`🗑️ Marking ${duplicateIds.length} duplicate queue entries as synced`);
      (async () => {
        try {
          await restUpdate('sync_queue', { synced: true }, { in: { id: duplicateIds } }, { returning: false });
        } catch (err) {
          console.error('Error marking duplicates as synced:', err);
        }
      })();
    }

    return Array.from(latestByKey.values());
  }

  /**
   * Sort sync queue entries by dependencies
   * Objects must be synced before contexts/blocks/properties that reference them
   * Creates must be synced before updates
   */
  private sortQueueByDependencies(queue: any[]): any[] {
    // Priority order: lower = sync first
    const tablePriority: Record<string, number> = {
      'objects': 1,
      'blocks': 2,
      'properties': 2,
      // Advanced properties (F058) - sync after base properties
      'property_options': 3,
      'property_formulas': 3,
      'property_rollups': 3,
      'property_relations': 3,
      'property_computed_cache': 4, // Cache last (depends on formulas/rollups)
      'block_reminders': 3,
      'contexts': 5,
    };

    const operationPriority: Record<string, number> = {
      'create': 1,
      'update': 2,
      'delete': 3,
    };

    return queue.sort((a, b) => {
      // First sort by table (objects before blocks/properties before contexts)
      const tablePriorityA = tablePriority[a.table_name] || 10;
      const tablePriorityB = tablePriority[b.table_name] || 10;
      if (tablePriorityA !== tablePriorityB) {
        return tablePriorityA - tablePriorityB;
      }

      // Then sort by operation (creates before updates before deletes)
      const opPriorityA = operationPriority[a.operation] || 10;
      const opPriorityB = operationPriority[b.operation] || 10;
      if (opPriorityA !== opPriorityB) {
        return opPriorityA - opPriorityB;
      }

      // Finally sort by timestamp
      return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
    });
  }

  /**
   * Sanitize block data by rounding position/size values to integers
   * PostgreSQL expects INTEGER types for x, y, width, height, z_index
   */
  private sanitizeBlockData(data: any): any {
    if (!data) return data;

    const sanitized = { ...data };

    // Round position and size fields to integers
    if (sanitized.x !== undefined) sanitized.x = Math.round(sanitized.x);
    if (sanitized.y !== undefined) sanitized.y = Math.round(sanitized.y);
    if (sanitized.width !== undefined) sanitized.width = Math.round(sanitized.width);
    if (sanitized.height !== undefined) sanitized.height = Math.round(sanitized.height);
    if (sanitized.z_index !== undefined) sanitized.z_index = Math.round(sanitized.z_index);

    return sanitized;
  }

  /** D8: mark a record as just-written-by-us so its realtime echo is ignored. */
  private markRecentlyWritten(table: string, id: string): void {
    if (!id) return;
    const now = Date.now();
    this.recentlyWritten.set(`${table}:${id}`, now);
    // Opportunistic prune so the map can't grow unbounded.
    if (this.recentlyWritten.size > 500) {
      const cutoff = now - SyncEngine.RECENTLY_WRITTEN_TTL_MS;
      for (const [k, ts] of this.recentlyWritten) {
        if (ts < cutoff) this.recentlyWritten.delete(k);
      }
    }
  }

  /** D8: true if a realtime event is an echo of our own recent write. */
  private isSelfEcho(table: string, id: string): boolean {
    const key = `${table}:${id}`;
    const ts = this.recentlyWritten.get(key);
    if (ts === undefined) return false;
    if (Date.now() - ts < SyncEngine.RECENTLY_WRITTEN_TTL_MS) return true;
    this.recentlyWritten.delete(key);
    return false;
  }

  /**
   * Apply a sync queue entry to Supabase
   */
  private async applySyncEntry(entry: any): Promise<void> {
    const { table_name, operation, record_id, data } = entry;
    // D8: record that we are about to write this so the realtime echo is skipped.
    this.markRecentlyWritten(table_name, record_id);

    switch (operation) {
      case 'create':
        // For blocks/properties, check if parent object exists first using direct REST API
        if ((table_name === 'blocks' || table_name === 'properties') && data.object_id) {
          const { data: parentObj, error: parentError } = await restSelectSingle('objects', {
            select: 'id',
            eq: { id: data.object_id }
          });

          if (parentError || !parentObj) {
            // Parent doesn't exist in Supabase - check if it exists locally
            // If it doesn't exist locally either, the object was deleted and this is orphaned
            const { db } = await import('../db/database');
            const localParent = await db.objects.get(data.object_id);
            if (!localParent) {
              console.warn(`⚠️ Orphaned ${table_name}:${record_id} - parent object ${data.object_id} deleted, marking as synced`);
              // Don't throw - let it be marked as synced so we stop retrying
              return;
            }

            // Parent exists locally but not in Supabase - try to sync it directly
            console.log(`🔧 Parent object ${data.object_id} missing from Supabase, attempting to sync it now...`);
            try {
              const userId = getUserId();
              const parentData = {
                id: localParent.id,
                user_id: userId,
                name: localParent.name,
                type: localParent.type,
                content: localParent.content || '',
                color: localParent.color,
                tags: localParent.tags || [],
                status: localParent.status || 'active',
                distillation: localParent.distillation || 0,
                source_type: localParent.sourceType,
                lifecycle: localParent.type === 'task' || localParent.type === 'meeting' ? 'ephemeral' : 'persistent',
                version: 1,
                created_at: localParent.createdAt || new Date().toISOString(),
                updated_at: localParent.updatedAt || new Date().toISOString(),
                deleted_at: localParent.deletedAt || null
              };

              const { error: parentInsertError } = await restInsert('objects', parentData, { returning: false });
              if (parentInsertError && parentInsertError.code !== '23505') {
                // If not a duplicate error, log and retry later
                console.warn(`⚠️ Failed to sync parent object ${data.object_id}:`, parentInsertError);
                throw new Error(`RETRY_LATER: Parent object sync failed`);
              }
              console.log(`✅ Parent object ${data.object_id} synced successfully, continuing with ${table_name}`);
            } catch (parentSyncError: any) {
              if (parentSyncError.message?.includes('RETRY_LATER')) {
                throw parentSyncError;
              }
              console.warn(`⚠️ Error syncing parent object:`, parentSyncError);
              throw new Error(`RETRY_LATER: Parent object not synced`);
            }
          }
        }

        // Include the record_id in the data for insert using direct REST API
        // Sanitize block data to ensure integers for position/size fields
        let sanitizedData = table_name === 'blocks' ? this.sanitizeBlockData(data) : data;
        // Ensure data is in snake_case format (defensive: should already be transformed from queueChange)
        sanitizedData = transformKeysToSnakeCase(sanitizedData);
        const insertData = { id: record_id, ...sanitizedData };
        console.log(`📤 Creating ${table_name}:${record_id}`, table_name === 'blocks' ? { content: sanitizedData.content?.substring(0, 50), object_id: sanitizedData.object_id } : {});
        // D2: idempotent creates. Upsert so a lost-response replay merges into
        // the existing row instead of erroring (no duplicate, no reliance on
        // string/code-matching the conflict). Properties key on their composite
        // unique key; objects/blocks key on the primary key.
        const insertOptions: { returning: boolean; upsert?: boolean; onConflict?: string } = { returning: false };
        if (table_name === 'properties') {
          insertOptions.upsert = true;
          insertOptions.onConflict = 'object_id,name,user_id';
        } else if (table_name === 'objects' || table_name === 'blocks') {
          insertOptions.upsert = true;
          insertOptions.onConflict = 'id';
        }
        const { error: insertError } = await restInsert(table_name, insertData, insertOptions);
        // Ignore 409 Conflict errors (duplicate contexts are OK)
        if (insertError) {
          if (insertError.code === '23505') {
            // Duplicate - already exists, that's fine
            console.log(`⏭️ Skipping duplicate ${table_name}:${record_id}`);
          } else if (insertError.code === '23503') {
            // Foreign key violation - parent object doesn't exist in Supabase
            // This shouldn't happen often since we check above, but handle it gracefully
            console.warn(`⚠️ Foreign key violation for CREATE ${table_name}:${record_id} (object_id: ${data.object_id}), marking as synced (orphaned)`);
            // Don't throw - let it be marked as synced so we stop retrying
          } else if (insertError.code === '42501') {
            // RLS policy violation - user doesn't have permission
            console.warn(`⚠️ RLS violation for ${table_name}:${record_id}, marking as synced`);
            // Don't throw - let it be marked as synced so we stop retrying
          } else {
            throw insertError;
          }
        } else {
          console.log(`✅ Created ${table_name}:${record_id}`);
        }
        break;

      case 'update':
        // For blocks/properties, check if parent object exists first using direct REST API
        if ((table_name === 'blocks' || table_name === 'properties') && data.object_id) {
          console.log(`🔍 Checking if parent object ${data.object_id} exists in Supabase for ${table_name}:${record_id}`);
          const { data: parentObjForUpdate, error: parentErrorForUpdate } = await restSelectSingle('objects', {
            select: 'id',
            eq: { id: data.object_id }
          });

          console.log(`🔍 Parent object lookup result:`, { found: !!parentObjForUpdate, error: parentErrorForUpdate?.message || null });

          if (parentErrorForUpdate || !parentObjForUpdate) {
            // Parent doesn't exist in Supabase - check if it exists locally
            const { db } = await import('../db/database');
            const localParent = await db.objects.get(data.object_id);
            console.log(`🔍 Local parent object lookup:`, { found: !!localParent, name: localParent?.name });
            if (!localParent) {
              console.warn(`⚠️ Orphaned ${table_name}:${record_id} - parent object ${data.object_id} deleted, marking as synced`);
              // Don't throw - let it be marked as synced so we stop retrying
              return;
            }

            // Parent exists locally but not in Supabase - try to sync it directly
            console.log(`🔧 Parent object ${data.object_id} missing from Supabase, attempting to sync it now...`);
            try {
              const updateUserId = getUserId();
              const parentDataForUpdate = {
                id: localParent.id,
                user_id: updateUserId,
                name: localParent.name,
                type: localParent.type,
                content: localParent.content || '',
                color: localParent.color,
                tags: localParent.tags || [],
                status: localParent.status || 'active',
                distillation: localParent.distillation || 0,
                source_type: localParent.sourceType,
                lifecycle: localParent.type === 'task' || localParent.type === 'meeting' ? 'ephemeral' : 'persistent',
                version: 1,
                created_at: localParent.createdAt || new Date().toISOString(),
                updated_at: localParent.updatedAt || new Date().toISOString(),
                deleted_at: localParent.deletedAt || null
              };

              const { error: parentInsertErrorForUpdate } = await restInsert('objects', parentDataForUpdate, { returning: false });
              if (parentInsertErrorForUpdate && parentInsertErrorForUpdate.code !== '23505') {
                // If not a duplicate error, log and retry later
                console.warn(`⚠️ Failed to sync parent object ${data.object_id}:`, parentInsertErrorForUpdate);
                throw new Error(`RETRY_LATER: Parent object sync failed`);
              }
              console.log(`✅ Parent object ${data.object_id} synced successfully, continuing with ${table_name}`);
            } catch (parentSyncErrorForUpdate: any) {
              if (parentSyncErrorForUpdate.message?.includes('RETRY_LATER')) {
                throw parentSyncErrorForUpdate;
              }
              console.warn(`⚠️ Error syncing parent object:`, parentSyncErrorForUpdate);
              throw new Error(`RETRY_LATER: Parent object not synced`);
            }
          }
        }

        // Check for conflicts using direct REST API
        const { data: serverData, error: selectError } = await restSelectSingle(table_name, {
          select: 'version,updated_at',
          eq: { id: record_id }
        });

        // If record doesn't exist in Supabase, create it instead (upsert behavior)
        if (selectError && (selectError.code === 'PGRST116' || selectError.message?.includes('no rows'))) {
          console.log(`📤 Record ${table_name}:${record_id} not found in Supabase, creating instead`, table_name === 'blocks' ? { contentLength: data.content?.length || 0, contentPreview: data.content?.substring(0, 50), object_id: data.object_id } : {});

          // For UPDATE operations that need to be converted to INSERT,
          // we need the full object data, not just the partial update
          let insertData: any = {
            id: record_id,
            ...data,
            version: 1,
            created_at: data.created_at || new Date().toISOString(),
            updated_at: new Date().toISOString()
          };

          // For objects, fetch full data from local DB since updates may be partial
          if (table_name === 'objects') {
            const { db } = await import('../db/database');
            const localObj = await db.objects.get(record_id);
            if (localObj) {
              const currentUserId = getUserId();
              insertData = {
                id: record_id,
                user_id: currentUserId || data.user_id,
                name: localObj.name,
                type: localObj.type,
                content: localObj.content || '',
                color: localObj.color,
                tags: localObj.tags || [],
                status: localObj.status || 'active',
                // Preserve source_type on updates. This is a full-row upsert
                // (onConflict: 'id'), so any column omitted here is overwritten
                // to null in Supabase. Omitting source_type previously wiped it
                // on the first post-create update (e.g. template layout / touch),
                // which broke the Reader gate for email + web-clip sources.
                source_type: localObj.sourceType ?? null,
                distillation: localObj.distillation || 0,
                lifecycle: localObj.type === 'task' || localObj.type === 'meeting' ? 'ephemeral' : 'persistent',
                version: 1,
                created_at: localObj.createdAt || new Date().toISOString(),
                updated_at: localObj.updatedAt || new Date().toISOString(),
                deleted_at: localObj.deletedAt || null
              };
              console.log(`📤 Fetched full object data from local DB for upsert:`, { name: localObj.name, type: localObj.type });
            }
          }

          // For blocks, fetch full data from local DB since updates may be partial
          if (table_name === 'blocks') {
            const { db } = await import('../db/database');
            const localBlock = await db.blocks.get(record_id);
            if (localBlock) {
              const blockUserId = getUserId();
              insertData = {
                id: record_id,
                object_id: localBlock.objectId,
                user_id: blockUserId || data.user_id,
                type: localBlock.type,
                content: localBlock.content || '',
                x: localBlock.x,
                y: localBlock.y,
                width: localBlock.width,
                height: localBlock.height,
                z_index: localBlock.zIndex,
                metadata: localBlock.metadata || {},
                styles: localBlock.styles,
                title: localBlock.title ?? null,
                icon: localBlock.icon || null,
                is_linkable: localBlock.isLinkable || false,
                name: localBlock.name || null,
                crdt_state: localBlock.crdtState || null,
                version: 1,
                created_at: localBlock.createdAt || new Date().toISOString(),
                updated_at: localBlock.updatedAt || new Date().toISOString()
              };
              // Sanitize block data to ensure integers for position/size fields
              insertData = this.sanitizeBlockData(insertData);
              console.log(`📤 Fetched full block data from local DB for upsert:`, {
                objectId: localBlock.objectId,
                type: localBlock.type,
                contentLength: localBlock.content?.length || 0
              });
            }
          }

          // For properties, fetch full data from local DB since updates may be partial
          if (table_name === 'properties') {
            const { db } = await import('../db/database');
            const localProperty = await db.properties.get(record_id);
            if (localProperty) {
              insertData = {
                id: record_id,
                object_id: localProperty.objectId,
                name: localProperty.name,
                type: localProperty.type,
                value: localProperty.value,
                user_id: data.user_id, // Required for upsert on composite key
                version: 1,
                created_at: localProperty.createdAt || new Date().toISOString(),
                updated_at: localProperty.updatedAt || new Date().toISOString()
              };
              console.log(`📤 Fetched full property data from local DB for upsert:`, {
                objectId: localProperty.objectId,
                name: localProperty.name,
                type: localProperty.type
              });
            } else {
              // Property doesn't exist locally - it was deleted, mark as synced
              console.warn(`⚠️ Property ${record_id} not found locally, marking as synced (deleted)`);
              return;
            }
          }

          // Transform insertData to snake_case before sending to Supabase
          const transformedInsertData = transformKeysToSnakeCase(insertData);
          // For properties, use upsert on composite key to prevent duplicates
          const upsertOptions: { returning: boolean; upsert?: boolean; onConflict?: string } = { returning: false };
          if (table_name === 'properties') {
            upsertOptions.upsert = true;
            upsertOptions.onConflict = 'object_id,name,user_id';
          }
          const { error: upsertError } = await restInsert(table_name, transformedInsertData, upsertOptions);
          if (upsertError && upsertError.code !== '23505') {
            // For foreign key violations, mark as orphaned and don't retry (parent object was deleted)
            if (upsertError.code === '23503') {
              console.warn(`⚠️ Orphaned ${table_name}:${record_id} - parent object doesn't exist, marking as synced to stop retrying`);
              // Don't throw - let it be marked as synced so we stop retrying forever
              return;
            }
            console.error(`Error creating ${table_name}:${record_id}:`, upsertError);
            throw upsertError;
          } else {
            console.log(`✅ Created ${table_name}:${record_id} via upsert`);
          }
        } else if (serverData && serverData.version > data.version) {
          // Conflict detected!
          console.warn(`⚠️ Conflict detected for ${table_name}:${record_id}`);
          await this.handleConflict(table_name, record_id, data, serverData);
        } else {
          // No conflict, update normally
          // For blocks, fetch fresh data from IndexedDB to ensure we send the latest content
          // This handles the case where user types quickly and multiple updates are queued
          let freshData = data;
          if (table_name === 'blocks') {
            const { db } = await import('../db/database');
            const localBlock = await db.blocks.get(record_id);
            if (localBlock) {
              const freshUserId = getUserId();
              freshData = {
                object_id: localBlock.objectId,
                user_id: freshUserId || data.user_id,
                type: localBlock.type,
                content: localBlock.content || '',
                x: localBlock.x,
                y: localBlock.y,
                width: localBlock.width,
                height: localBlock.height,
                z_index: localBlock.zIndex,
                metadata: localBlock.metadata || {},
                styles: localBlock.styles,
                title: localBlock.title ?? null,
                icon: localBlock.icon || null,
                is_linkable: localBlock.isLinkable || false,
                name: localBlock.name || null,
                crdt_state: localBlock.crdtState || null,
                deleted_at: localBlock.deletedAt || null, // Soft delete support
                updated_at: localBlock.updatedAt,
                version: data.version
              };
              // Sanitize block data to ensure integers for position/size fields
              freshData = this.sanitizeBlockData(freshData);
              console.log(`📤 Fetched fresh block data from IndexedDB:`, {
                contentLength: freshData.content?.length || 0,
                contentPreview: freshData.content?.substring(0, 50),
                crdt_state_length: (localBlock.crdtState || '').length,
                crdt_state_present: !!localBlock.crdtState
              });
            }
          }

          // For properties, fetch fresh data from IndexedDB to ensure we send the latest value
          if (table_name === 'properties') {
            const { db } = await import('../db/database');
            const localProperty = await db.properties.get(record_id);
            if (localProperty) {
              freshData = {
                object_id: localProperty.objectId,
                name: localProperty.name,
                type: localProperty.type,
                value: localProperty.value,
                deleted_at: localProperty.deletedAt || null, // Soft delete support
                updated_at: localProperty.updatedAt,
                version: data.version
              };
              console.log(`📤 Fetched fresh property data from IndexedDB:`, {
                objectId: localProperty.objectId,
                name: localProperty.name
              });
            } else {
              // Property was deleted locally, skip the update
              console.warn(`⚠️ Property ${record_id} not found locally, skipping update (deleted)`);
              return;
            }
          }

          let updatePayload = {
            ...freshData,
            version: (freshData.version || 0) + 1,
            updated_at: new Date().toISOString()
          };
          // Transform updatePayload to snake_case before sending to Supabase
          updatePayload = transformKeysToSnakeCase(updatePayload);
          console.log(`📤 Updating ${table_name}:${record_id}`, table_name === 'blocks' ? { contentLength: updatePayload.content?.length || 0, contentPreview: updatePayload.content?.substring(0, 50), object_id: updatePayload.object_id } : {});
          const { error: updateError } = await restUpdate(table_name, updatePayload, { eq: { id: record_id } }, { returning: false });

          if (updateError) {
            console.error(`Error updating ${table_name}:${record_id}:`, updateError);
            // Don't throw on foreign key violations (orphaned records)
            if (updateError.code !== '23503') {
              throw updateError;
            }
          } else {
            console.log(`✅ Updated ${table_name}:${record_id}`);
          }
        }
        break;

      case 'delete': {
        // Soft delete using direct REST API
        // Also update updated_at so mobile pullChanges detects the deletion via its time-window filter
        const deletedAt = new Date().toISOString();
        await restUpdate(table_name, { deleted_at: deletedAt, updated_at: deletedAt }, { eq: { id: record_id } }, { returning: false });
        break;
      }
    }
  }

  /**
   * Sync down: Pull remote changes to local
   */
  private async syncDown(): Promise<void> {
    console.log('📥 [syncDown] Starting...');
    addBreadcrumb('Sync started', 'sync', { direction: 'down', lastSyncAt: this.lastSyncAt });

    // Get user ID directly from localStorage - bypasses stuck Supabase auth client
    console.log('📥 [syncDown] Getting user from localStorage...');
    const userId = getUserId();
    if (!userId) {
      console.log('📥 [syncDown] No user in localStorage, returning');
      return;
    }
    console.log('📥 [syncDown] User found:', userId);

    // Get last sync timestamp from local storage
    let lastSyncAt = localStorage.getItem('lastSyncAt') || new Date(0).toISOString();

    // Increment sync count
    this.syncCount++;

    // First sync of session: always do a full sync to catch any objects from other devices
    // This handles the case where mobile creates objects while web app was closed
    // Also do a full sync every 20 syncs (~5 minutes) to catch cross-device changes
    const shouldDoFullSync = this.isFirstSyncOfSession || (this.syncCount % 20 === 0);

    if (shouldDoFullSync) {
      if (this.isFirstSyncOfSession) {
        console.log('📥 [syncDown] First sync of session - doing full sync to catch cross-device changes');
        this.isFirstSyncOfSession = false;
      } else {
        console.log('📥 [syncDown] Periodic full sync (every ~5 min) to catch cross-device changes');
      }
      lastSyncAt = new Date(0).toISOString();
    }

    // Detect cleared IndexedDB scenario: DB is empty but we have a lastSyncAt timestamp
    // This means the user cleared IndexedDB manually or it was corrupted
    const localDBEmpty = await this.isLocalDBEmpty();
    if (localDBEmpty && lastSyncAt !== new Date(0).toISOString()) {
      console.warn('⚠️ IndexedDB is empty but lastSyncAt exists - resetting for full sync');
      localStorage.removeItem('lastSyncAt');
      lastSyncAt = new Date(0).toISOString();
    }

    console.log(`📥 [syncDown] Syncing changes since ${lastSyncAt}...`);

    // D1 — Guarded, non-destructive down-sync.
    // Build the set of record_ids that still have an UNSYNCED queue entry. The
    // pull must never overwrite a local row whose edit hasn't pushed yet
    // (local-wins-keep-pending) — that overwrite is how local-only edits were
    // silently destroyed. We let the pending push resolve those rows instead.
    const pendingRecordIds = new Set<string>();
    {
      const { data: pendingQueue } = await withTimeout(
        restSelect('sync_queue', {
          select: 'record_id',
          eq: { user_id: userId, synced: false }
        }),
        10000,
        'syncDown.fetchPending'
      );
      for (const row of pendingQueue || []) {
        if (row?.record_id) pendingRecordIds.add(row.record_id);
      }
      if (pendingRecordIds.size > 0) {
        console.log(`📥 [syncDown] ${pendingRecordIds.size} records have pending local changes — will not overwrite them`);
      }
    }

    // Fetch updated objects using direct REST API (check both updated_at and created_at)
    // Use restSelectAll to paginate past PostgREST's default 1000-row limit
    console.log('📥 [syncDown] Fetching objects from Supabase...');
    const { data: objects, error: objectsError } = await withTimeout(
      restSelectAll('objects', {
        select: '*',
        eq: { user_id: userId },
        or: `updated_at.gte.${lastSyncAt},created_at.gte.${lastSyncAt}`,
        order: 'updated_at.asc'
      }),
      30000,
      'syncDown.fetchObjects'
    );
    console.log('📥 [syncDown] Objects fetch complete');

    if (objectsError) {
      console.error('❌ Error fetching objects:', objectsError);
    }

    console.log(`📥 Found ${objects?.length || 0} objects to sync`);

    if (objects && objects.length > 0) {
      console.log(`📥 Received ${objects.length} updated objects`);

      for (const obj of objects) {
        // Transform snake_case to camelCase for local storage using helper
        const localObj = transformKeysToCamelCase(obj);

        // Skip deleted objects - don't add them to local DB
        if (obj.deleted_at || localObj.deletedAt) {
          console.log(`📥 Skipping deleted object: ${localObj.name} (${obj.id})`);
          // Remove from local if it exists
          const existing = await db.objects.get(obj.id);
          if (existing) {
            console.log(`📥 Removing deleted object from local: ${localObj.name} (${obj.id})`);
            await db.objects.delete(obj.id);
            // Also remove associated data (blocks, properties, links, attachments)
            await db.blocks.where('objectId').equals(obj.id).delete();
            await db.properties.where('objectId').equals(obj.id).delete();
            await db.links.where('fromObjectId').equals(obj.id).delete();
            await db.links.where('toObjectId').equals(obj.id).delete();
            await db.attachments.where('objectId').equals(obj.id).delete();
          }
          continue;
        }

        // Ensure required fields have defaults (in case transformation missed them)
        localObj.content = localObj.content || '';
        localObj.tags = localObj.tags || [];
        localObj.status = localObj.status || 'active';
        localObj.version = localObj.version || 1;

        // Update local IndexedDB
        const existing = await db.objects.get(obj.id);

        if (!existing) {
          // New object from server
          console.log(`📥 Adding new object from server: ${localObj.name} (${localObj.id})`);
          await db.objects.add(localObj as any);
        } else if (pendingRecordIds.has(obj.id)) {
          // D1: local row has an unsynced edit — keep it, let the push resolve it.
          console.log(`📥 Skipping overwrite of object with pending local change: ${obj.id}`);
        } else if (new Date(obj.updated_at) > new Date(existing.updatedAt)) {
          // Server version is newer — preserve local-only fields not in Supabase
          if (existing.icon && !localObj.icon) {
            localObj.icon = existing.icon;
          }
          await db.objects.put(localObj as any);
        }
      }
    }

    // Fetch updated blocks using direct REST API
    // Note: blocks table doesn't have user_id column - RLS handles filtering via objects join
    // Use restSelectAll to paginate past PostgREST's default 1000-row limit
    console.log('📥 [syncDown] Fetching blocks from Supabase...');
    const { data: blocks, error: blocksError } = await withTimeout(
      restSelectAll('blocks', {
        select: '*',
        gte: { updated_at: lastSyncAt },
        order: 'updated_at.asc'
      }),
      30000,
      'syncDown.fetchBlocks'
    );
    console.log('📥 [syncDown] Blocks fetch complete');

    if (blocksError) {
      console.error('❌ Error fetching blocks:', blocksError);
    }

    let blocksChanged = false;
    if (blocks && blocks.length > 0) {
      console.log(`📥 Received ${blocks.length} updated blocks`);

      for (const block of blocks) {
        // Transform snake_case to camelCase (+ normalize null title) for local storage
        const localBlock = transformBlockToLocal(block);

        // Skip deleted blocks - don't add them to local DB
        if (block.deleted_at || localBlock.deletedAt) {
          console.log(`📥 Skipping deleted block: ${block.id}`);
          // Remove from local if it exists
          const existing = await db.blocks.get(block.id);
          if (existing) {
            console.log(`📥 Removing deleted block from local: ${block.id}`);
            await db.blocks.delete(block.id);
            blocksChanged = true;
          }
          continue;
        }

        const existing = await db.blocks.get(block.id);

        if (!existing) {
          // New block from server — pre-warm Y.Doc if it has crdt_state
          if (block.crdt_state && TEXT_CRDT_TYPES.includes(block.type)) {
            initYDocFromState(block.id, block.crdt_state);
          }
          await db.blocks.add(localBlock);
          blocksChanged = true;
        } else if (block.crdt_state && existing.crdtState && TEXT_CRDT_TYPES.includes(block.type)) {
          // Both sides have CRDT state — merge instead of LWW to preserve offline edits
          try {
            initYDocFromState(block.id, existing.crdtState); // load local state first
            const mergedContent = applyRemoteUpdate(block.id, block.crdt_state); // merge remote
            const mergedCrdtState = encodeYDocState(block.id); // capture merged state
            const mergedBlock = {
              ...localBlock,
              content: mergedContent || localBlock.content,
              crdtState: mergedCrdtState,
            };
            // title/icon/styles are not CRDT-merged — resolve them by last-write-wins.
            // localBlock carries the incoming remote values (via transformKeysToCamelCase);
            // keep the local copy only when our edit is newer than the remote one.
            const remoteIsNewer = new Date(block.updated_at) > new Date(existing.updatedAt);
            if (!remoteIsNewer) {
              mergedBlock.title = existing.title;
              mergedBlock.icon = existing.icon;
              mergedBlock.styles = existing.styles;
            }
            await db.blocks.put(mergedBlock);
            blocksChanged = true;
          } catch (err) {
            console.warn(`[SyncEngine] syncDown Yjs merge failed for block ${block.id}, falling back to LWW:`, err);
            if (new Date(block.updated_at) > new Date(existing.updatedAt)) {
              // Remote is newer — let its title/icon/styles win (already on localBlock).
              await db.blocks.put(localBlock);
              blocksChanged = true;
            }
          }
        } else if (pendingRecordIds.has(block.id)) {
          // D1: non-CRDT block has an unsynced local edit — keep it, let the
          // push resolve it. (CRDT text blocks above are conflict-free merged.)
          console.log(`📥 Skipping overwrite of block with pending local change: ${block.id}`);
        } else if (new Date(block.updated_at) > new Date(existing.updatedAt)) {
          // No CRDT state — use timestamp-based last-write-wins.
          // Remote is newer, so its title/icon/styles (already on localBlock) win.
          await db.blocks.put(localBlock);
          blocksChanged = true;
        }
      }
    }

    // Fetch updated properties using direct REST API
    // Note: properties table doesn't have user_id column - RLS handles filtering via objects join
    // Use restSelectAll to paginate past PostgREST's default 1000-row limit
    console.log('📥 [syncDown] Fetching properties from Supabase...');
    const { data: properties, error: propertiesError } = await withTimeout(
      restSelectAll('properties', {
        select: '*',
        gte: { updated_at: lastSyncAt },
        order: 'updated_at.asc'
      }),
      30000,
      'syncDown.fetchProperties'
    );
    console.log('📥 [syncDown] Properties fetch complete');

    if (propertiesError) {
      console.error('❌ Error fetching properties:', propertiesError);
    }

    let propertiesChanged = false;
    if (properties && properties.length > 0) {
      console.log(`📥 Received ${properties.length} updated properties`);

      for (const property of properties) {
        // Transform snake_case to camelCase for local storage using helper
        const localProperty = transformKeysToCamelCase(property);

        // Skip deleted properties - don't add them to local DB
        if (property.deleted_at || localProperty.deletedAt) {
          console.log(`📥 Skipping deleted property: ${property.name} (${property.id})`);
          // Remove from local if it exists
          const existing = await db.properties.get(property.id);
          if (existing) {
            console.log(`📥 Removing deleted property from local: ${property.name} (${property.id})`);
            await db.properties.delete(property.id);
            propertiesChanged = true;
          }
          continue;
        }

        // Check if property already exists in local DB
        const existing = await db.properties.get(property.id);
        if (!existing) {
          // New property from server
          await db.properties.add(localProperty);
          propertiesChanged = true;
          console.log(`➕ Added property from server: ${property.name} (${property.type})`);
        } else if (pendingRecordIds.has(property.id)) {
          // D1: property has an unsynced local change — keep it, let the push resolve it.
          console.log(`📥 Skipping overwrite of property with pending local change: ${property.id}`);
        } else if (new Date(property.updated_at) > new Date(existing.updatedAt)) {
          // Server version is newer
          await db.properties.put(localProperty);
          propertiesChanged = true;
          console.log(`✏️ Updated property from server: ${property.name} (${property.type})`);
        }
      }
    }

    // Update last sync timestamp
    localStorage.setItem('lastSyncAt', new Date().toISOString());

    // Fetch updated property_relations (F058)
    console.log('📥 [syncDown] Fetching property_relations from Supabase...');
    const { data: propertyRelations, error: relationsError } = await withTimeout(
      restSelectAll('property_relations', {
        select: '*',
        gte: { created_at: lastSyncAt }, // Relations use created_at only
        order: 'created_at.asc'
      }),
      15000,
      'syncDown.fetchPropertyRelations'
    );
    console.log('📥 [syncDown] Property relations fetch complete');

    if (relationsError) {
      console.error('❌ Error fetching property_relations:', relationsError);
    }

    let relationsChanged = false;
    if (propertyRelations && propertyRelations.length > 0) {
      console.log(`📥 [syncDown] Storing ${propertyRelations.length} property relations...`);

      for (const relation of propertyRelations) {
        try {
          const localRelation = {
            id: relation.id,
            propertyId: relation.property_id,
            sourceObjectId: relation.source_object_id,
            targetObjectId: relation.target_object_id,
            position: relation.position,
            createdAt: relation.created_at,
          };

          await db.propertyRelations.put(localRelation);
          relationsChanged = true;
        } catch (error) {
          console.error(`❌ Error storing property relation ${relation.id}:`, error);
        }
      }
    }

    // Fetch updated property_options (F058)
    console.log('📥 [syncDown] Fetching property_options from Supabase...');
    const { data: propertyOptions, error: optionsError } = await withTimeout(
      restSelectAll('property_options', {
        select: '*',
        gte: { updated_at: lastSyncAt },
        order: 'updated_at.asc'
      }),
      15000,
      'syncDown.fetchPropertyOptions'
    );
    console.log('📥 [syncDown] Property options fetch complete');

    if (optionsError) {
      console.error('❌ Error fetching property_options:', optionsError);
    }

    let optionsChanged = false;
    if (propertyOptions && propertyOptions.length > 0) {
      console.log(`📥 [syncDown] Storing ${propertyOptions.length} property options...`);

      for (const option of propertyOptions) {
        try {
          await db.propertyOptions.put(option);
          optionsChanged = true;
        } catch (error) {
          console.error(`❌ Error storing property option ${option.id}:`, error);
        }
      }
    }

    // Fetch updated property_formulas (F058)
    console.log('📥 [syncDown] Fetching property_formulas from Supabase...');
    const { data: propertyFormulas, error: formulasError } = await withTimeout(
      restSelectAll('property_formulas', {
        select: '*',
        gte: { updated_at: lastSyncAt },
        order: 'updated_at.asc'
      }),
      15000,
      'syncDown.fetchPropertyFormulas'
    );
    console.log('📥 [syncDown] Property formulas fetch complete');

    if (formulasError) {
      console.error('❌ Error fetching property_formulas:', formulasError);
    }

    let formulasChanged = false;
    if (propertyFormulas && propertyFormulas.length > 0) {
      console.log(`📥 [syncDown] Storing ${propertyFormulas.length} property formulas...`);

      for (const formula of propertyFormulas) {
        try {
          await db.propertyFormulas.put(formula);
          formulasChanged = true;
        } catch (error) {
          console.error(`❌ Error storing property formula ${formula.id}:`, error);
        }
      }
    }

    // Fetch updated property_rollups (F058)
    console.log('📥 [syncDown] Fetching property_rollups from Supabase...');
    const { data: propertyRollups, error: rollupsError } = await withTimeout(
      restSelectAll('property_rollups', {
        select: '*',
        gte: { updated_at: lastSyncAt },
        order: 'updated_at.asc'
      }),
      15000,
      'syncDown.fetchPropertyRollups'
    );
    console.log('📥 [syncDown] Property rollups fetch complete');

    if (rollupsError) {
      console.error('❌ Error fetching property_rollups:', rollupsError);
    }

    let rollupsChanged = false;
    if (propertyRollups && propertyRollups.length > 0) {
      console.log(`📥 [syncDown] Storing ${propertyRollups.length} property rollups...`);

      for (const rollup of propertyRollups) {
        try {
          await db.propertyRollups.put(rollup);
          rollupsChanged = true;
        } catch (error) {
          console.error(`❌ Error storing property rollup ${rollup.id}:`, error);
        }
      }
    }

    // Fetch updated property_computed_cache (F058)
    console.log('📥 [syncDown] Fetching property_computed_cache from Supabase...');
    const { data: propertyCache, error: cacheError } = await withTimeout(
      restSelectAll('property_computed_cache', {
        select: '*',
        gte: { computed_at: lastSyncAt },
        order: 'computed_at.asc'
      }),
      15000,
      'syncDown.fetchPropertyComputedCache'
    );
    console.log('📥 [syncDown] Property computed cache fetch complete');

    if (cacheError) {
      console.error('❌ Error fetching property_computed_cache:', cacheError);
    }

    let cacheChanged = false;
    if (propertyCache && propertyCache.length > 0) {
      console.log(`📥 [syncDown] Storing ${propertyCache.length} property cache entries...`);

      for (const cache of propertyCache) {
        try {
          const localCache = {
            id: cache.id,
            propertyId: cache.property_id,
            objectId: cache.object_id,
            computed_value: cache.computed_value,
            computed_at: cache.computed_at,
            is_stale: cache.is_stale,
          };

          await db.propertyComputedCache.put(localCache);
          cacheChanged = true;
        } catch (error) {
          console.error(`❌ Error storing property cache ${cache.id}:`, error);
        }
      }
    }

    // ---- Block Reminders ----
    console.log('📥 [syncDown] Fetching block_reminders from Supabase...');
    const { data: reminders, error: remindersError } = await withTimeout(
      restSelectAll('block_reminders', {
        select: '*',
        gte: { updated_at: lastSyncAt },
        order: 'updated_at.asc'
      }),
      15000,
      'syncDown.fetchBlockReminders'
    );
    console.log('📥 [syncDown] Block reminders fetch complete');

    if (remindersError) {
      console.error('❌ Error fetching block_reminders:', remindersError);
    }

    let remindersChanged = false;
    if (reminders && reminders.length > 0) {
      console.log(`📥 [syncDown] Storing ${reminders.length} block reminders...`);

      for (const rem of reminders) {
        try {
          const localReminder = {
            id: rem.id,
            blockId: rem.block_id,
            objectId: rem.object_id,
            reminderAt: rem.reminder_at,
            note: rem.note || undefined,
            status: rem.status,
            firedAt: rem.fired_at || undefined,
            dismissedAt: rem.dismissed_at || undefined,
            createdAt: rem.created_at,
            updatedAt: rem.updated_at,
          };

          await db.reminders.put(localReminder);
          remindersChanged = true;
        } catch (error) {
          console.error(`❌ Error storing block reminder ${rem.id}:`, error);
        }
      }
    }

    // Notify if any data changed
    const objectsChanged = objects && objects.length > 0;
    const advancedPropsChanged = relationsChanged || optionsChanged || formulasChanged || rollupsChanged || cacheChanged;

    if (objectsChanged || blocksChanged || propertiesChanged || advancedPropsChanged || remindersChanged) {
      console.log(`📥 [syncDown] Data changed - objects: ${objects?.length || 0}, blocks: ${blocksChanged}, properties: ${propertiesChanged}, advanced props: ${advancedPropsChanged}, reminders: ${remindersChanged}`);
      this.notifyDataChange();
    }
  }

  /**
   * Handle sync conflict
   */
  private async handleConflict(
    table: string,
    recordId: string,
    localData: any,
    serverData: any
  ): Promise<void> {
    const userId = getUserId();
    if (!userId) return;

    // Store conflict for user review using direct REST API
    await restInsert('conflicts', {
      user_id: userId,
      object_id: recordId,
      local_version: localData,
      local_timestamp: localData.updated_at,
      server_version: serverData,
      server_timestamp: serverData.updated_at,
      resolved: false
    }, { returning: false });

    // For now: Last-write-wins (server wins)
    // User can review conflicts later
    console.log(`⚠️ Conflict stored for review: ${table}:${recordId}`);
  }

  /**
   * Set up real-time subscriptions
   */
  private async setupRealtime(): Promise<void> {
    // Use getUserId from localStorage to avoid hanging on Supabase client
    const userId = getUserId();
    if (!userId) return;

    try {
      // Subscribe to changes in all core tables
      this.realtimeChannel = supabase
        .channel('db-changes')
        // Objects table
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'objects',
            filter: `user_id=eq.${userId}`
          },
          (payload) => {
            console.log('🔔 Real-time update (objects):', payload);
            this.handleRealtimeUpdate(payload);
          }
        )
        // Blocks table
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'blocks',
            filter: `user_id=eq.${userId}`
          },
          (payload) => {
            console.log('🔔 Real-time update (blocks):', payload);
            this.handleRealtimeUpdate(payload);
          }
        )
        // Properties table
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'properties',
            filter: `user_id=eq.${userId}`
          },
          (payload) => {
            console.log('🔔 Real-time update (properties):', payload);
            this.handleRealtimeUpdate(payload);
          }
        )
        // Contexts table (relationships/links)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'contexts',
            filter: `user_id=eq.${userId}`
          },
          (payload) => {
            console.log('🔔 Real-time update (contexts):', payload);
            this.handleRealtimeUpdate(payload);
          }
        )
        // Property Relations table (F058)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'property_relations'
          },
          (payload) => {
            console.log('🔔 Real-time update (property_relations):', payload);
            this.handleRealtimeUpdate(payload);
          }
        )
        // Property Options table (F058)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'property_options'
          },
          (payload) => {
            console.log('🔔 Real-time update (property_options):', payload);
            this.handleRealtimeUpdate(payload);
          }
        )
        // Property Formulas table (F058)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'property_formulas'
          },
          (payload) => {
            console.log('🔔 Real-time update (property_formulas):', payload);
            this.handleRealtimeUpdate(payload);
          }
        )
        // Property Rollups table (F058)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'property_rollups'
          },
          (payload) => {
            console.log('🔔 Real-time update (property_rollups):', payload);
            this.handleRealtimeUpdate(payload);
          }
        )
        // Property Computed Cache table (F058)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'property_computed_cache'
          },
          (payload) => {
            console.log('🔔 Real-time update (contexts):', payload);
            this.handleRealtimeUpdate(payload);
          }
        )
        // User collections
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'user_collections',
            filter: `user_id=eq.${userId}`
          },
          (payload) => {
            console.log('🔔 Real-time update (user_collections):', payload);
            this.handleRealtimeUpdate(payload);
          }
        )
        // Collection items
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'user_collection_items',
            filter: `user_id=eq.${userId}`
          },
          (payload) => {
            console.log('🔔 Real-time update (user_collection_items):', payload);
            this.handleRealtimeUpdate(payload);
          }
        )
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            console.log('📡 Realtime connected — pulling changes missed while disconnected');
            // Pull anything we missed while the channel was establishing
            this.sync({ skipAuthCheck: true });
          }
          if (status === 'CLOSED' || status === 'CHANNEL_ERROR') {
            console.warn('⚠️ Realtime channel dropped, pulling missed changes');
            this.sync({ skipAuthCheck: true });
          }
        });

      console.log('📡 Real-time subscriptions active (objects, blocks, properties, contexts, collections)');
    } catch (error) {
      // Realtime is nice to have but not required - sync will still work via polling
      console.warn('⚠️ Could not set up realtime subscriptions:', error);
    }
  }

  /**
   * Handle real-time update from Supabase (batched to prevent UI lag)
   */
  private async handleRealtimeUpdate(payload: any): Promise<void> {
    // Add to pending updates queue
    this.pendingUpdates.push(payload);

    // Clear any existing batch timeout
    if (this.updateBatchTimeout) {
      clearTimeout(this.updateBatchTimeout);
    }

    // Process updates in batch after 200ms of no new updates
    this.updateBatchTimeout = setTimeout(() => {
      this.processPendingUpdates();
    }, 200);
  }

  /**
   * Process all pending real-time updates in a batch
   */
  private async processPendingUpdates(): Promise<void> {
    if (this.pendingUpdates.length === 0) return;

    const updates = [...this.pendingUpdates];
    this.pendingUpdates = [];

    console.log(`📦 Processing ${updates.length} batched updates`);

    for (const payload of updates) {
      const { eventType, new: newRecord, old: oldRecord, table } = payload;

      // D8: skip our own echo. After we push a record, Supabase realtime sends
      // it straight back; reapplying it is redundant and can fight the LWW/dirty
      // guards under clock skew. Only applies to id-keyed tables.
      const echoId = newRecord?.id || oldRecord?.id;
      if (echoId && this.isSelfEcho(table, echoId)) {
        console.log(`🔇 Skipping self-echo realtime ${eventType} ${table}:${echoId}`);
        continue;
      }

      try {
        // Route to appropriate table handler
        switch (table) {
          case 'objects':
            await this.processObjectUpdate(eventType, newRecord, oldRecord);
            break;
          case 'blocks':
            await this.processBlockUpdate(eventType, newRecord, oldRecord);
            break;
          case 'properties':
            await this.processPropertyUpdate(eventType, newRecord, oldRecord);
            break;
          case 'contexts':
            await this.processContextUpdate(eventType, newRecord, oldRecord);
            break;
          case 'user_collections':
            await this.processCollectionUpdate(eventType, newRecord, oldRecord);
            break;
          case 'user_collection_items':
            await this.processCollectionItemUpdate(eventType, newRecord, oldRecord);
            break;
          default:
            console.warn(`⚠️ Unknown table in realtime update: ${table}`);
        }
      } catch (error) {
        console.error(`Error processing ${table} update:`, error);
      }
    }

    // Notify listeners once after all updates are processed
    this.notifyListeners();

    // Notify data change listeners so UI can refresh
    if (updates.length > 0) {
      this.notifyDataChange();
    }
  }

  /**
   * Process real-time object update
   */
  private async processObjectUpdate(eventType: string, newRecord: any, oldRecord: any): Promise<void> {
    switch (eventType) {
      case 'INSERT':
        // Transform snake_case to camelCase for IndexedDB
        const insertObj = transformKeysToCamelCase(newRecord);
        await db.objects.add(insertObj);
        console.log(`➕ Added object from remote: ${newRecord.name}`);
        break;

      case 'UPDATE':
        const existing = await db.objects.get(newRecord.id);
        if (existing && this.locallyDirty.has(newRecord.id)) {
          // D1: object has an unsynced local edit — keep it, let the push resolve it.
          console.log(`✏️ Skipping realtime overwrite of object with pending local change: ${newRecord.id}`);
        } else if (!existing || new Date(newRecord.updated_at) > new Date(existing.updatedAt)) {
          // Transform snake_case to camelCase for IndexedDB
          const updateObj = transformKeysToCamelCase(newRecord);
          // Preserve local-only fields not in Supabase
          if (existing?.icon && !updateObj.icon) {
            updateObj.icon = existing.icon;
          }
          await db.objects.put(updateObj);
          console.log(`✏️ Updated object from remote: ${newRecord.name}`);
        }
        break;

      case 'DELETE':
        await db.objects.delete(oldRecord.id);
        console.log(`🗑️ Deleted object from remote: ${oldRecord.name}`);
        break;
    }
  }

  /**
   * Process real-time block update
   */
  private async processBlockUpdate(eventType: string, newRecord: any, oldRecord: any): Promise<void> {
    switch (eventType) {
      case 'INSERT': {
        // Transform snake_case to camelCase (+ normalize null title) for IndexedDB
        const insertBlock = transformBlockToLocal(newRecord);
        // Pre-warm the Y.Doc if crdt_state is present so future merges start from correct state
        if (newRecord.crdt_state) {
          initYDocFromState(newRecord.id, newRecord.crdt_state);
        }
        await db.blocks.add(insertBlock);
        console.log(`➕ Added block from remote: ${newRecord.id}`);
        break;
      }

      case 'UPDATE': {
        const existing = await db.blocks.get(newRecord.id);

        // Soft-delete handling: if remote UPDATE sets deleted_at, hard-remove from local
        // Prevents the "block reappears after ~1 minute" bug where LWW would re-put
        // a soft-deleted record and leave it lingering in IndexedDB.
        if (newRecord.deleted_at) {
          if (existing) {
            await db.blocks.delete(newRecord.id);
            console.log(`🗑️ Removed soft-deleted block from local (realtime): ${newRecord.id}`);
          }
          break;
        }

        // Yjs CRDT merge: if remote has crdt_state and block type supports it, merge instead of LWW
        if (newRecord.crdt_state && TEXT_CRDT_TYPES.includes(newRecord.type)) {
          try {
            const mergedContent = applyRemoteUpdate(newRecord.id, newRecord.crdt_state);
            const updateBlock = {
              ...transformBlockToLocal(newRecord),
              content: mergedContent || newRecord.content,
            };
            // title/icon/styles aren't CRDT-merged — last-write-wins. Keep the
            // local copy only when our edit is newer than this remote one.
            const remoteIsNewer = !existing || new Date(newRecord.updated_at) > new Date(existing.updatedAt);
            if (existing && !remoteIsNewer) {
              updateBlock.title = existing.title;
              updateBlock.icon = existing.icon;
              updateBlock.styles = existing.styles;
            }
            await db.blocks.put(updateBlock);
            console.log(`✏️ Updated block from remote (Yjs merge): ${newRecord.id}`);
          } catch (err) {
            console.warn(`[SyncEngine] Yjs merge failed for block ${newRecord.id}, falling back to LWW:`, err);
            if (!existing || new Date(newRecord.updated_at) > new Date(existing.updatedAt)) {
              // Remote is newer — its title/icon/styles win (already on the record).
              await db.blocks.put(transformBlockToLocal(newRecord));
            }
          }
        } else if (existing && this.locallyDirty.has(newRecord.id)) {
          // D1: non-CRDT block has an unsynced local edit — keep it, let the push resolve it.
          console.log(`✏️ Skipping realtime overwrite of block with pending local change: ${newRecord.id}`);
        } else if (!existing || new Date(newRecord.updated_at) > new Date(existing.updatedAt)) {
          // Remote is newer — LWW, its title/icon/styles win (already on the record).
          await db.blocks.put(transformBlockToLocal(newRecord));
          console.log(`✏️ Updated block from remote (LWW): ${newRecord.id}`);
        }
        break;
      }

      case 'DELETE':
        await db.blocks.delete(oldRecord.id);
        console.log(`🗑️ Deleted block from remote: ${oldRecord.id}`);
        break;
    }
  }

  /**
   * Process real-time property update
   */
  private async processPropertyUpdate(eventType: string, newRecord: any, oldRecord: any): Promise<void> {
    switch (eventType) {
      case 'INSERT':
        // Transform snake_case to camelCase for IndexedDB
        const insertProp = transformKeysToCamelCase(newRecord);
        await db.properties.add(insertProp);
        console.log(`➕ Added property from remote: ${newRecord.id}`);
        break;

      case 'UPDATE':
        const existing = await db.properties.get(newRecord.id);
        // Soft-delete handling: if remote UPDATE sets deleted_at, hard-remove from local
        if (newRecord.deleted_at) {
          if (existing) {
            await db.properties.delete(newRecord.id);
            console.log(`🗑️ Removed soft-deleted property from local (realtime): ${newRecord.id}`);
          }
          break;
        }
        if (!existing || new Date(newRecord.updated_at) > new Date(existing.updatedAt)) {
          // Transform snake_case to camelCase for IndexedDB
          const updateProp = transformKeysToCamelCase(newRecord);
          await db.properties.put(updateProp);
          console.log(`✏️ Updated property from remote: ${newRecord.id}`);
        }
        break;

      case 'DELETE':
        await db.properties.delete(oldRecord.id);
        console.log(`🗑️ Deleted property from remote: ${oldRecord.id}`);
        break;
    }
  }

  /**
   * Process real-time context (link/relationship) update
   */
  private async processContextUpdate(eventType: string, newRecord: any, oldRecord: any): Promise<void> {
    switch (eventType) {
      case 'INSERT': {
        // Map Supabase context fields to IndexedDB link fields
        // context_id = container (fromObjectId), object_id = contained item (toObjectId)
        const link = {
          id: newRecord.id,
          fromObjectId: newRecord.context_id,
          toObjectId: newRecord.object_id,
          linkType: newRecord.context_type === 'primary' ? 'contains' : 'references',
          createdAt: newRecord.created_at,
        };
        // Skip if already exists locally (offline-first: local link was already created)
        const existingLink = await db.links.get(newRecord.id);
        if (!existingLink) {
          await db.links.add(link);
          console.log(`➕ Added context from remote: ${newRecord.id}`);
        }
        break;
      }

      case 'UPDATE': {
        const existing = await db.links.get(newRecord.id);
        if (!existing || new Date(newRecord.updated_at) > new Date(existing.updatedAt || existing.createdAt)) {
          const link = {
            id: newRecord.id,
            fromObjectId: newRecord.context_id,
            toObjectId: newRecord.object_id,
            linkType: newRecord.context_type === 'primary' ? 'contains' : 'references',
            createdAt: newRecord.created_at,
          };
          await db.links.put(link);
          console.log(`✏️ Updated context from remote: ${newRecord.id}`);
        }
        break;
      }

      case 'DELETE':
        await db.links.delete(oldRecord.id);
        console.log(`🗑️ Deleted context from remote: ${oldRecord.id}`);
        break;
    }
  }

  /**
   * Process real-time collection update
   */
  private async processCollectionUpdate(eventType: string, newRecord: any, oldRecord: any): Promise<void> {
    switch (eventType) {
      case 'INSERT':
        // Transform snake_case to camelCase for IndexedDB
        const insertCollection = transformKeysToCamelCase(newRecord);
        await db.userCollections.add(insertCollection);
        console.log(`➕ Added collection from remote: ${newRecord.name}`);
        break;

      case 'UPDATE':
        const existing = await db.userCollections.get(newRecord.id);
        if (!existing || new Date(newRecord.updated_at) > new Date(existing.updatedAt)) {
          // Transform snake_case to camelCase for IndexedDB
          const updateCollection = transformKeysToCamelCase(newRecord);
          await db.userCollections.put(updateCollection);
          console.log(`✏️ Updated collection from remote: ${newRecord.name}`);
        }
        break;

      case 'DELETE':
        await db.userCollections.delete(oldRecord.id);
        console.log(`🗑️ Deleted collection from remote: ${oldRecord.name}`);
        break;
    }
  }

  /**
   * Process real-time collection item update
   */
  private async processCollectionItemUpdate(eventType: string, newRecord: any, oldRecord: any): Promise<void> {
    switch (eventType) {
      case 'INSERT':
        // Transform snake_case to camelCase for IndexedDB
        const insertItem = transformKeysToCamelCase(newRecord);
        await db.userCollectionItems.add(insertItem);
        console.log(`➕ Added collection item from remote: ${newRecord.id}`);
        break;

      case 'UPDATE':
        const existing = await db.userCollectionItems.get(newRecord.id);
        if (!existing || new Date(newRecord.updated_at) > new Date(existing.updatedAt)) {
          // Transform snake_case to camelCase for IndexedDB
          const updateItem = transformKeysToCamelCase(newRecord);
          await db.userCollectionItems.put(updateItem);
          console.log(`✏️ Updated collection item from remote: ${newRecord.id}`);
        }
        break;

      case 'DELETE':
        await db.userCollectionItems.delete(oldRecord.id);
        console.log(`🗑️ Deleted collection item from remote: ${oldRecord.id}`);
        break;
    }
  }

  /**
   * Get current sync status (cached for performance)
   */
  async getStatus(): Promise<SyncStatus> {
    // Return cached status if available and fresh (less than 5 seconds old)
    if (this.cachedStatus) {
      return this.cachedStatus;
    }

    const userId = getUserId();

    if (!userId) {
      const status: SyncStatus = {
        isOnline: navigator.onLine,
        isSyncing: false,
        lastSyncAt: null,
        pendingChanges: 0,
        failedChanges: 0,
        queuedChanges: [],
        oldestPending: null,
        authHealth: getAuthHealthStatus()
      };
      this.cacheStatus(status);
      return status;
    }

    // Get pending (not yet synced, not permanently failed) using direct REST API
    const { data: pendingQueue } = await restSelect('sync_queue', {
      select: '*',
      eq: { user_id: userId, synced: false },
      is: { failed_at: null },
      order: 'timestamp.asc'
    });

    // Get permanently failed entries using direct REST API
    const { data: failedQueue } = await restSelect('sync_queue', {
      select: 'id',
      eq: { user_id: userId },
      not: { failed_at: null }
    });

    const lastSyncAt = localStorage.getItem('lastSyncAt');
    const oldestPending = pendingQueue && pendingQueue.length > 0
      ? new Date(pendingQueue[0].timestamp)
      : null;

    const status: SyncStatus = {
      isOnline: navigator.onLine,
      isSyncing: this.syncInProgress,
      lastSyncAt: lastSyncAt ? new Date(lastSyncAt) : null,
      pendingChanges: pendingQueue?.length || 0,
      failedChanges: failedQueue?.length || 0,
      queuedChanges: pendingQueue || [],
      oldestPending,
      authHealth: getAuthHealthStatus()
    };

    this.cacheStatus(status);
    return status;
  }

  /**
   * Get detailed sync queue statistics
   */
  async getSyncQueueStats(): Promise<SyncQueueStats> {
    const userId = getUserId();
    if (!userId) {
      return { pending: 0, failed: 0, retrying: 0, oldestPending: null, oldestFailed: null };
    }

    // Get pending entries (not synced, not failed) using direct REST API
    const { data: pending } = await restSelect('sync_queue', {
      select: 'timestamp',
      eq: { user_id: userId, synced: false },
      is: { failed_at: null },
      order: 'timestamp.asc'
    });

    // Get failed entries using direct REST API
    const { data: failed } = await restSelect('sync_queue', {
      select: 'failed_at',
      eq: { user_id: userId },
      not: { failed_at: null },
      order: 'failed_at.asc'
    });

    // Get entries currently retrying (have retry_count > 0 but not failed) using direct REST API
    const { data: retrying } = await restSelect('sync_queue', {
      select: 'id',
      eq: { user_id: userId, synced: false },
      is: { failed_at: null },
      gt: { retry_count: 0 }
    });

    return {
      pending: pending?.length || 0,
      failed: failed?.length || 0,
      retrying: retrying?.length || 0,
      oldestPending: pending && pending.length > 0 ? new Date(pending[0].timestamp) : null,
      oldestFailed: failed && failed.length > 0 ? new Date(failed[0].failed_at) : null
    };
  }

  /**
   * Cache status for 5 seconds to prevent excessive queries
   */
  private cacheStatus(status: SyncStatus): void {
    this.cachedStatus = status;

    // Clear existing timeout
    if (this.statusCacheTimeout) {
      clearTimeout(this.statusCacheTimeout);
    }

    // Invalidate cache after 5 seconds
    this.statusCacheTimeout = setTimeout(() => {
      this.cachedStatus = null;
    }, 5000);
  }

  /**
   * Subscribe to sync status changes
   */
  onStatusChange(callback: (status: SyncStatus) => void): () => void {
    this.listeners.add(callback);

    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Subscribe to data change notifications (called when sync brings in new data)
   */
  onDataChange(callback: () => void): () => void {
    this.dataChangeListeners.add(callback);

    // Return unsubscribe function
    return () => {
      this.dataChangeListeners.delete(callback);
    };
  }

  /**
   * Notify all data change listeners and dispatch browser event
   * Call this after syncDown writes new data to IndexedDB
   */
  private dataChangeTimeout: ReturnType<typeof setTimeout> | null = null;

  private notifyDataChange(): void {
    // Debounce data change notifications to prevent UI thrashing during sync
    if (this.dataChangeTimeout) clearTimeout(this.dataChangeTimeout);
    this.dataChangeTimeout = setTimeout(() => {
      console.log('📢 Notifying data change listeners...');

      // Notify registered callbacks
      this.dataChangeListeners.forEach(listener => {
        try {
          listener();
        } catch (e) {
          console.error('Error in data change listener:', e);
        }
      });

      // Emit eventBus events so pages react to sync changes
      // Pages listen for object:updated to refresh their data
      eventBus.emit('sync:completed', { timestamp: Date.now() }, 'sync');
      eventBus.emit('object:updated', { source: 'sync' }, 'sync');

      // Also dispatch browser event for components that prefer event listeners
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('pkm-data-changed', {
          detail: { timestamp: Date.now() }
        }));
      }
    }, 1000);
  }

  /**
   * Notify all listeners of status change (debounced to prevent UI lag)
   */
  private notifyListeners(): void {
    // Clear any pending notification
    if (this.notifyTimeout) {
      clearTimeout(this.notifyTimeout);
    }

    // Debounce notifications by 3 seconds to prevent UI lag during sync and dragging
    this.notifyTimeout = setTimeout(() => {
      // Invalidate cache before getting fresh status
      this.cachedStatus = null;
      this.getStatus().then(status => {
        this.listeners.forEach(listener => listener(status));
      });
    }, 3000);
  }

  /**
   * Get or create device ID
   */
  private getDeviceId(): string {
    let deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
      deviceId = `device-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('deviceId', deviceId);
    }
    return deviceId;
  }

  /**
   * Force sync now
   */
  async syncNow(): Promise<void> {
    await this.sync();
  }

  /**
   * Clear all pending (unsynced) entries from the sync queue
   * WARNING: This will lose any pending changes that haven't been synced!
   */
  async clearAllPending(): Promise<number> {
    const userId = getUserId();
    if (!userId) {
      console.warn('Not authenticated, cannot clear sync queue');
      return 0;
    }

    // Get count first using direct REST API
    const { data: pending } = await restSelect('sync_queue', {
      select: 'id',
      eq: { user_id: userId, synced: false }
    });

    const count = pending?.length || 0;

    if (count === 0) {
      console.log('✅ No pending entries to clear');
      return 0;
    }

    // Delete all unsynced entries from the queue using direct REST API
    const { error } = await restDelete('sync_queue', {
      eq: { user_id: userId, synced: false }
    }, { returning: false });

    if (error) {
      console.error('Failed to clear sync queue:', error);
      return 0;
    }

    console.log(`✅ Cleared ${count} pending sync queue entries`);
    this.notifyListeners();
    return count;
  }

  /**
   * Clear only permanently failed entries (those that exceeded max retries)
   */
  async clearFailedEntries(): Promise<number> {
    const userId = getUserId();
    if (!userId) {
      console.warn('Not authenticated, cannot clear failed entries');
      return 0;
    }

    // Get count first using direct REST API
    const { data: failed } = await restSelect('sync_queue', {
      select: 'id',
      eq: { user_id: userId },
      not: { failed_at: null }
    });

    const count = failed?.length || 0;

    if (count === 0) {
      console.log('✅ No failed entries to clear');
      return 0;
    }

    // Delete permanently failed entries using direct REST API
    const { error } = await restDelete('sync_queue', {
      eq: { user_id: userId },
      not: { failed_at: null }
    }, { returning: false });

    if (error) {
      console.error('Failed to clear failed entries:', error);
      return 0;
    }

    console.log(`✅ Cleared ${count} permanently failed sync queue entries`);
    this.notifyListeners();
    return count;
  }

  /**
   * Retry all failed entries by resetting their retry count
   */
  async retryFailedEntries(): Promise<number> {
    const userId = getUserId();
    if (!userId) {
      console.warn('Not authenticated, cannot retry failed entries');
      return 0;
    }

    // Get count first using direct REST API
    const { data: failed } = await restSelect('sync_queue', {
      select: 'id',
      eq: { user_id: userId },
      not: { failed_at: null }
    });

    const count = failed?.length || 0;

    if (count === 0) {
      console.log('✅ No failed entries to retry');
      return 0;
    }

    // Reset retry count and clear failed status using direct REST API
    const { error } = await restUpdate('sync_queue', {
      retry_count: 0,
      failed_at: null,
      last_error: null,
      next_retry_at: null
    }, {
      eq: { user_id: userId },
      not: { failed_at: null }
    }, { returning: false });

    if (error) {
      console.error('Failed to retry failed entries:', error);
      return 0;
    }

    console.log(`✅ Reset ${count} failed entries for retry`);
    this.notifyListeners();

    // Trigger immediate sync
    this.sync();

    return count;
  }

  /**
   * Clear all failed sync queue entries (legacy method - now calls clearAllPending)
   * @deprecated Use clearAllPending() or clearFailedEntries() instead
   */
  async clearFailedSyncQueue(): Promise<void> {
    await this.clearAllPending();
  }

  /**
   * Clean up orphaned blocks and properties from local database
   * Removes entries that reference objects that no longer exist
   */
  async cleanupOrphanedLocalData(): Promise<{ blocksRemoved: number; propertiesRemoved: number }> {
    console.log('🧹 Cleaning up orphaned local data...');

    // Get all object IDs
    const allObjects = await db.objects.toArray();
    const objectIds = new Set(allObjects.map(obj => obj.id));

    // Find and remove orphaned blocks
    const allBlocks = await db.blocks.toArray();
    const orphanedBlocks = allBlocks.filter(block => !objectIds.has(block.objectId));

    if (orphanedBlocks.length > 0) {
      const orphanedBlockIds = orphanedBlocks.map(b => b.id);
      await db.blocks.bulkDelete(orphanedBlockIds);
      console.log(`🗑️ Removed ${orphanedBlocks.length} orphaned blocks`);
    }

    // Find and remove orphaned properties
    const allProperties = await db.properties.toArray();
    const orphanedProperties = allProperties.filter(prop => !objectIds.has(prop.objectId));

    if (orphanedProperties.length > 0) {
      const orphanedPropertyIds = orphanedProperties.map(p => p.id);
      await db.properties.bulkDelete(orphanedPropertyIds);
      console.log(`🗑️ Removed ${orphanedProperties.length} orphaned properties`);
    }

    console.log('✅ Local cleanup complete');
    return {
      blocksRemoved: orphanedBlocks.length,
      propertiesRemoved: orphanedProperties.length
    };
  }

  /**
   * Full cleanup: clear failed sync queue and remove orphaned local data
   */
  async fullCleanup(): Promise<void> {
    console.log('🧹 Starting full cleanup...');

    // Clear failed sync queue entries
    await this.clearFailedSyncQueue();

    // Clean up orphaned local data
    await this.cleanupOrphanedLocalData();

    console.log('✅ Full cleanup complete');
    this.notifyListeners();
  }

  /**
   * Remove duplicate contexts from Supabase
   * Keeps only the oldest context for each unique (object_id, context_id, context_type) combination
   */
  async removeDuplicateContexts(): Promise<void> {
    const userId = getUserId();
    if (!userId) {
      console.warn('Not authenticated, cannot remove duplicates');
      return;
    }

    console.log('🧹 Checking for duplicate contexts...');

    // Get all contexts for this user using direct REST API
    const { data: contexts, error } = await restSelect('contexts', {
      select: '*',
      order: 'created_at.asc'
    });

    if (error) {
      console.error('Failed to fetch contexts:', error);
      return;
    }

    if (!contexts) {
      console.log('No contexts found');
      return;
    }

    // Group by (object_id, context_id, context_type)
    const seen = new Map<string, string>(); // key -> first context id
    const duplicates: string[] = [];

    for (const context of contexts) {
      const key = `${context.object_id}|${context.context_id}|${context.context_type}`;

      if (seen.has(key)) {
        // This is a duplicate, mark for deletion
        duplicates.push(context.id);
      } else {
        // First occurrence, keep it
        seen.set(key, context.id);
      }
    }

    if (duplicates.length === 0) {
      console.log('✅ No duplicate contexts found');
      return;
    }

    console.log(`🗑️ Found ${duplicates.length} duplicate contexts, removing...`);

    // Delete duplicates using direct REST API
    const { error: deleteError } = await restDelete('contexts', {
      in: { id: duplicates }
    }, { returning: false });

    if (deleteError) {
      console.error('Failed to delete duplicates:', deleteError);
    } else {
      console.log(`✅ Removed ${duplicates.length} duplicate contexts`);
    }
  }

  /**
   * Debug helper: Test Supabase connectivity
   * Call from console: window.syncEngine.debugConnectivity()
   */
  async debugConnectivity(): Promise<void> {
    console.log('🔍 === SUPABASE CONNECTIVITY DEBUG ===');

    // Test 1: Auth
    console.log('🔍 Test 1: Checking auth...');
    try {
      const start1 = Date.now();
      const { data: { user }, error: authError } = await withTimeout(
        supabase.auth.getUser(),
        5000,
        'debug.getUser'
      );
      console.log(`✅ Auth check completed in ${Date.now() - start1}ms`);
      if (authError) {
        console.error('❌ Auth error:', authError);
      } else if (user) {
        console.log('✅ User:', user.id, user.email);
      } else {
        console.log('⚠️ No user found (not authenticated)');
        return;
      }
    } catch (e) {
      console.error('❌ Auth test failed:', e);
      return;
    }

    // Test 2: Simple query via Supabase client
    console.log('🔍 Test 2: Querying objects table via Supabase client...');
    try {
      const start2 = Date.now();
      const { data, error } = await withTimeout(
        Promise.resolve(
          supabase.from('objects').select('id, name').limit(3)
        ),
        10000,
        'debug.queryObjects'
      );
      console.log(`✅ Query completed in ${Date.now() - start2}ms`);
      if (error) {
        console.error('❌ Query error:', error);
      } else {
        console.log('✅ Objects:', data);
      }
    } catch (e) {
      console.error('❌ Query test failed:', e);
    }

    // Test 3: Direct fetch (bypass Supabase client)
    console.log('🔍 Test 3: Direct fetch (bypassing Supabase JS client)...');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const start3 = Date.now();
      const response = await fetch(`${supabaseUrl}/rest/v1/objects?select=id,name&limit=3`, {
        headers: {
          'apikey': supabaseKey,
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      console.log(`✅ Direct fetch completed in ${Date.now() - start3}ms, status: ${response.status}`);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Direct fetch result:', data);
      } else {
        const errorText = await response.text();
        console.error('❌ Direct fetch error:', response.status, errorText);
      }
    } catch (e) {
      console.error('❌ Direct fetch test failed:', e);
    }

    // Test 4: Check sync_queue table
    console.log('🔍 Test 4: Querying sync_queue table...');
    try {
      const start4 = Date.now();
      const { data, error } = await withTimeout(
        Promise.resolve(
          supabase.from('sync_queue').select('id').limit(1)
        ),
        10000,
        'debug.querySyncQueue'
      );
      console.log(`✅ sync_queue query completed in ${Date.now() - start4}ms`);
      if (error) {
        console.error('❌ sync_queue error:', error);
      } else {
        console.log('✅ sync_queue accessible, entries:', data?.length || 0);
      }
    } catch (e) {
      console.error('❌ sync_queue test failed:', e);
    }

    console.log('🔍 === DEBUG COMPLETE ===');
  }
}

// Singleton instance
export const syncEngine = new SyncEngine();

// Expose to window for debugging
if (typeof window !== 'undefined') {
  (window as any).syncEngine = syncEngine;
}

// Auto-initialize when module loads (if authenticated)
// Use getUserId() from localStorage to bypass problematic Supabase client
if (typeof window !== 'undefined') {
  // Small delay to ensure localStorage is ready
  setTimeout(() => {
    const userId = getUserId();
    if (userId) {
      console.log('[SyncEngine] User found in localStorage, initializing...');
      syncEngine.initialize();
    } else {
      console.log('[SyncEngine] No user in localStorage, waiting for sign-in');
    }
  }, 100);
}

// Listen for storage events to detect login/logout from other tabs
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === 'pkm_user_id') {
      if (e.newValue) {
        console.log('[SyncEngine] User logged in (storage event), initializing...');
        syncEngine.initialize();
      } else {
        console.log('[SyncEngine] User logged out (storage event), destroying...');
        syncEngine.destroy();
      }
    }
  });
}

export default syncEngine;
